import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Plus, Trash2 } from 'lucide-react'
import type { ContentItem } from '../../api/contentApi'

/* eslint-disable @typescript-eslint/no-explicit-any */
type Payload = Record<string, any>

interface ArtistEditorProps {
  item: ContentItem
  onSave: (payload: Payload) => void
  saving?: boolean
}

export function ArtistEditor({ item, onSave, saving }: ArtistEditorProps) {
  const [p, setP] = useState<Payload>((item.payload as Payload) || {})
  useEffect(() => {
    setP((item.payload as Payload) || {})
  }, [item.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: string, v: any) => setP((prev) => ({ ...prev, [k]: v }))
  const arr = (k: string): string[] => (Array.isArray(p[k]) ? p[k] : [])
  const setArr = (k: string, csv: string) => set(k, csv.split(',').map((s) => s.trim()).filter(Boolean))

  const faq: Payload[] = Array.isArray(p.faq) ? p.faq : []
  const setFaq = (next: Payload[]) => set('faq', next)
  const updateFaq = (i: number, key: string, v: string) => setFaq(faq.map((f, idx) => (idx === i ? { ...f, [key]: v } : f)))
  const themes: string[] = Array.isArray(p.themes) ? p.themes : []

  return (
    <div className="space-y-5">
      {p.hero_image_url ? (
        <img src={String(p.hero_image_url)} alt="" className="w-full h-48 object-cover rounded-md border" />
      ) : null}

      <div className="grid grid-cols-2 gap-4">
        <Field label="Headline"><Input value={String(p.headline || '')} onChange={(e) => set('headline', e.target.value)} /></Field>
        <Field label="Subtitle"><Input value={String(p.subtitle || '')} onChange={(e) => set('subtitle', e.target.value)} /></Field>
        <Field label="Location"><Input value={String(p.location || '')} onChange={(e) => set('location', e.target.value)} /></Field>
        <Field label="Known for"><Input value={String(p.known_for || '')} onChange={(e) => set('known_for', e.target.value)} /></Field>
      </div>

      <Field label="Bio"><Textarea rows={6} value={String(p.bio || '')} onChange={(e) => set('bio', e.target.value)} /></Field>
      <Field label="Visual voice (style & themes)"><Textarea rows={3} value={String(p.visual_voice || '')} onChange={(e) => set('visual_voice', e.target.value)} /></Field>
      <Field label="Pull quote"><Textarea rows={2} value={String(p.pull_quote || '')} onChange={(e) => set('pull_quote', e.target.value)} /></Field>

      <div className="grid grid-cols-2 gap-4">
        <Field label={`SEO title (${String(p.seo_title || '').length}/60)`}>
          <Input value={String(p.seo_title || '')} onChange={(e) => set('seo_title', e.target.value)} />
        </Field>
        <Field label={`SEO description (${String(p.seo_description || '').length}/160)`}>
          <Input value={String(p.seo_description || '')} onChange={(e) => set('seo_description', e.target.value)} />
        </Field>
      </div>

      <Field label="Characteristics (comma-separated)">
        <Input value={arr('characteristics').join(', ')} onChange={(e) => setArr('characteristics', e.target.value)} />
      </Field>
      <Field label="Keywords (comma-separated)">
        <Input value={arr('keywords').join(', ')} onChange={(e) => setArr('keywords', e.target.value)} />
      </Field>

      {themes.length > 0 && (
        <Field label="Graph themes (read-only — used for artwork grouping)">
          <div className="flex flex-wrap gap-1.5">
            {themes.map((t) => (
              <span key={t} className="text-xs bg-muted rounded-full px-2 py-0.5">{t}</span>
            ))}
          </div>
        </Field>
      )}

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>FAQ</Label>
          <Button variant="outline" size="sm" onClick={() => setFaq([...faq, { q: '', a: '' }])}>
            <Plus className="h-4 w-4 mr-1" />
            Add question
          </Button>
        </div>
        {faq.map((f, i) => (
          <div key={i} className="rounded-md border p-4 space-y-2">
            <Input value={String(f.q || '')} onChange={(e) => updateFaq(i, 'q', e.target.value)} placeholder="Question" />
            <Textarea rows={2} value={String(f.a || '')} onChange={(e) => updateFaq(i, 'a', e.target.value)} placeholder="Answer" />
            <div className="flex justify-end">
              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => setFaq(faq.filter((_, idx) => idx !== i))}>
                <Trash2 className="h-4 w-4 mr-1" />
                Remove
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <Button onClick={() => onSave(p)} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  )
}
